import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header1 from "./Header/Header1";
import { useAuth } from "../AuthContext";

/* ─────────────── STYLES ─────────────── */
const Styles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Google+Sans:wght@400;500;600&family=Roboto:wght@300;400;500&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    .gm-root {
      min-height: 100vh;
      background: black;
      font-family: 'Google Sans', 'Roboto', sans-serif;
      color: #e8eaed;
    }

    /* ── PAGE LAYOUT ── */
    .gm-body {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: calc(100vh - 64px);
      padding: 24px;
      gap: 48px;
    }

    @media (max-width: 820px) {
      .gm-body { flex-direction: column; gap: 24px; }
    }

    /* ── VIDEO COLUMN ── */
    .gm-video-col {
      flex: 1;
      max-width: 560px;
      min-width: 300px;
    }

    .gm-video-wrap {
      position: relative;
      width: 100%;
      aspect-ratio: 4/3;
      background: #3c4043;
      border-radius: 16px;
      overflow: hidden;
    }

    .gm-video-wrap video {
      width: 100%; height: 100%;
      object-fit: cover;
      display: block;
      transform: scaleX(-1); /* mirror like meet */
    }

    /* avatar when cam off */
    .gm-avatar-overlay {
      position: absolute; inset: 0;
      display: flex; align-items: center; justify-content: center;
      background: #3c4043;
    }
    .gm-avatar-circle {
      width: 96px; height: 96px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      font-size: 38px; font-weight: 500; color: #fff;
      background: #5f6368;
      user-select: none;
    }

    /* name tag bottom left */
    .gm-nametag {
      position: absolute; bottom: 14px; left: 14px;
      background: rgba(0,0,0,0.6);
      backdrop-filter: blur(4px);
      padding: 4px 12px; border-radius: 6px;
      font-size: 13px; font-weight: 500; color: #fff;
      letter-spacing: 0.1px;
    }

    /* ── FLOATING CONTROLS ── */
    .gm-controls {
      position: absolute; bottom: 14px;
      left: 50%; transform: translateX(-50%);
      display: flex; gap: 10px; align-items: center;
    }

    .gm-ctrl-btn {
      width: 44px; height: 44px; border-radius: 50%; border: none;
      display: flex; align-items: center; justify-content: center;
      cursor: pointer; transition: all 0.15s ease;
      backdrop-filter: blur(6px);
      outline: none;
    }
    .gm-ctrl-btn.active {
      background: rgba(255,255,255,0.18);
      color: #fff;
    }
    .gm-ctrl-btn.active:hover { background: rgba(255,255,255,0.28); }
    .gm-ctrl-btn.inactive {
      background: #ea4335;
      color: #fff;
    }
    .gm-ctrl-btn.inactive:hover { background: #d93025; }

    /* ── INFO COLUMN ── */
    .gm-info-col {
      display: flex; flex-direction: column;
      gap: 24px;
      min-width: 280px;
      max-width: 340px;
    }

    .gm-heading {
      font-size: 28px; font-weight: 400; color: #e8eaed;
      line-height: 1.3;
    }
    .gm-heading span {
      color: #8ab4f8;
      font-weight: 500;
    }

    .gm-subtext {
      font-size: 14px; color: #9aa0a6; font-weight: 400;
      line-height: 1.5; margin-top: -14px;
    }

    /* expert card */
    .gm-expert-card {
      display: flex; align-items: center; gap: 14px;
      background: #2d2f31;
      border: 1px solid #3c4043;
      border-radius: 12px;
      padding: 14px 16px;
    }
    .gm-expert-avatar {
      width: 44px; height: 44px; border-radius: 50%;
      background: linear-gradient(135deg, #1a73e8, #0d47a1);
      display: flex; align-items: center; justify-content: center;
      font-size: 18px; font-weight: 600; color: #fff; flex-shrink: 0;
    }
    .gm-expert-label {
      font-size: 11px; color: #9aa0a6; text-transform: uppercase;
      letter-spacing: 0.8px; font-weight: 500;
    }
    .gm-expert-name {
      font-size: 15px; font-weight: 500; color: #e8eaed; margin-top: 2px;
    }

    /* status row */
    .gm-status-row {
      display: flex; align-items: center; gap: 8px;
      font-size: 13px; color: #9aa0a6;
    }
    .gm-status-dot {
      width: 8px; height: 8px; border-radius: 50%;
      background: #34a853;
      animation: gm-pulse 2s ease-in-out infinite;
      flex-shrink: 0;
    }
    @keyframes gm-pulse {
      0%,100% { opacity: 1; } 50% { opacity: 0.4; }
    }

    /* join button */
    .gm-join-btn {
      width: 100%;
      padding: 13px 0; border-radius: 24px; border: none;
      background: #1a73e8;
      color: #fff; font-size: 15px; font-weight: 500;
      font-family: 'Google Sans', sans-serif;
      cursor: pointer; letter-spacing: 0.15px;
      transition: all 0.18s ease;
      box-shadow: 0 1px 3px rgba(0,0,0,0.3), 0 4px 8px rgba(26,115,232,0.25);
    }
    .gm-join-btn:hover {
      background: #1765cc;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3), 0 6px 16px rgba(26,115,232,0.35);
      transform: translateY(-1px);
    }
    .gm-join-btn:active {
      transform: translateY(0);
      background: #1558b0;
    }

    /* device hint */
    .gm-device-hint {
      font-size: 12px; color: #5f6368; text-align: center;
    }

    /* mic/cam state labels under controls */
    .gm-ctrl-labels {
      display: flex; justify-content: center; gap: 32px;
      margin-top: 8px;
    }
    .gm-ctrl-label {
      font-size: 11px; color: #9aa0a6; text-align: center;
    }
    .gm-ctrl-label.off { color: #ea4335; }
  `}</style>
);

/* ── SVG Icons ── */
const MicOnIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1-9c0-.55.45-1 1-1s1 .45 1 1v6c0 .55-.45 1-1 1s-1-.45-1-1V5z"/>
    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
  </svg>
);
const MicOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.34 3 3 3 .23 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z"/>
  </svg>
);
const CamOnIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z"/>
  </svg>
);
const CamOffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
    <path d="M21 6.5l-4 4V7c0-.55-.45-1-1-1H9.82L21 17.18V6.5zM3.27 2L2 3.27 4.73 6H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.21 0 .39-.08.54-.18L19.73 21 21 19.73 3.27 2z"/>
  </svg>
);

/* ══════════════════════════════════════════════════════════ */

const PreJoin1v1 = () => {
  const videoRef  = useRef(null);
  const streamRef = useRef(null);

  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { userId, logout } = useAuth();

  const bookingId  = params.get("bookingId");
  const expertName = params.get("expert_name");

  const [micOn, setMicOn]   = useState(true);
  const [camOn, setCamOn]   = useState(true);
  const [userName, setUserName] = useState("");

  // Fetch user name
  useEffect(() => {
    if (!userId) return;
    fetch(`${process.env.REACT_APP_API_URL}/get_user_data/?user_id=${userId}`)
      .then(r => r.json())
      .then(d => setUserName(d.name || ""))
      .catch(console.error);
  }, [userId]);

  // Camera preview
  useEffect(() => {
    const init = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (err) { console.error(err); }
    };
    init();
    return () => streamRef.current?.getTracks().forEach(t => t.stop());
  }, []);

  const toggleMic = () => {
    const next = !micOn;
    streamRef.current?.getAudioTracks().forEach(t => t.enabled = next);
    setMicOn(next);
  };

  const toggleCam = () => {
    const next = !camOn;
    streamRef.current?.getVideoTracks().forEach(t => t.enabled = next);
    if (next && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
    setCamOn(next);
  };

  const handleJoin = () => {
    sessionStorage.setItem(
    "prejoinSettings",
    JSON.stringify({
      micOn,
      camOn,
      userName,
      expertName
    })
  );
    navigate(`/meeting/${bookingId}`, {
      state: { micOn, camOn, userName, expertName }
    });
  };

  const initial     = userName?.charAt(0)?.toUpperCase() || "U";
  const expertInit  = expertName?.charAt(0)?.toUpperCase() || "E";

  return (
    <>
      <Styles />
      <div className="gm-root">
        <Header1 userId={userId} onLogout={logout} />

        <div className="gm-body">

          {/* ── LEFT: VIDEO PREVIEW ── */}
          <div className="gm-video-col">
            <div className="gm-video-wrap">

              {/* video always mounted, hidden via CSS when cam off */}
              <video
                ref={videoRef}
                autoPlay muted playsInline
                style={{ display: camOn ? "block" : "none" }}
              />

              {/* avatar when cam off */}
              {!camOn && (
                <div className="gm-avatar-overlay">
                  <div className="gm-avatar-circle">{initial}</div>
                </div>
              )}

              {/* name tag */}
              <div className="gm-nametag">{userName || "You"}</div>

              {/* floating icon controls */}
              <div className="gm-controls">
                <button
                  className={`gm-ctrl-btn ${micOn ? "active" : "inactive"}`}
                  onClick={toggleMic}
                  title={micOn ? "Turn off mic" : "Turn on mic"}
                >
                  {micOn ? <MicOnIcon /> : <MicOffIcon />}
                </button>
                <button
                  className={`gm-ctrl-btn ${camOn ? "active" : "inactive"}`}
                  onClick={toggleCam}
                  title={camOn ? "Turn off camera" : "Turn on camera"}
                >
                  {camOn ? <CamOnIcon /> : <CamOffIcon />}
                </button>
              </div>
            </div>

            {/* state labels under controls */}
            <div className="gm-ctrl-labels">
              <span className={`gm-ctrl-label ${micOn ? "" : "off"}`}>
                {micOn ? "Microphone on" : "Microphone off"}
              </span>
              <span className={`gm-ctrl-label ${camOn ? "" : "off"}`}>
                {camOn ? "Camera on" : "Camera off"}
              </span>
            </div>
          </div>

          {/* ── RIGHT: JOIN INFO ── */}
          <div className="gm-info-col">
            <div>
              <h1 className="gm-heading">
                Ready to<br />
                <span>join?</span>
              </h1>
              <p className="gm-subtext" style={{ marginTop: 8 }}>
                Your 1-on-1 session is about to begin
              </p>
            </div>

            {/* expert card */}
            <div className="gm-expert-card">
              <div className="gm-expert-avatar">{expertInit}</div>
              <div>
                <div className="gm-expert-label">Connecting with</div>
                <div className="gm-expert-name">{expertName || "Expert"}</div>
              </div>
            </div>

            {/* live status */}
            <div className="gm-status-row">
              <span className="gm-status-dot" />
              Session is ready to start
            </div>

            {/* join */}
            <button className="gm-join-btn" onClick={handleJoin}>
              Join now
            </button>

            <p className="gm-device-hint">
              Make sure your mic and camera are configured above
            </p>
          </div>

        </div>
      </div>
    </>
  );
};

export default PreJoin1v1;